import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Table,
  Button,
  Icon,
  Label,
  Menu,
  Modal,
  Form,
  Message,
  Dropdown,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders, fetchApi } from '../../api';

export default function ContributorAdmin() {
  const [activeTab, setActiveTab] = useState('contributors');
  const [contributors, setContributors] = useState([]);
  const [invites, setInvites] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Invite form state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLanguages, setInviteLanguages] = useState([]);
  const [inviteNotes, setInviteNotes] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);

  // Available languages for the invite dropdown
  const [availableLanguages, setAvailableLanguages] = useState([]);

  // Application review modal state
  const [reviewApp, setReviewApp] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');

  useEffect(() => {
    loadData();
    loadLanguages();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [contribRes, inviteRes, applyRes] = await Promise.all([
        fetch(`${getApiPath()}/@polyglot/contributors`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${getApiPath()}/@polyglot/contributors/invites`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${getApiPath()}/@polyglot/contributors/applications`, {
          headers: getAuthHeaders(),
        }),
      ]);

      if (contribRes.status === 401 || inviteRes.status === 401) {
        window.location.href = `/login?return_url=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
      if (contribRes.status === 403 || inviteRes.status === 403) {
        setError('You do not have permission to manage contributors.');
        setLoading(false);
        return;
      }

      const contribData = await contribRes.json();
      const inviteData = await inviteRes.json();
      const applyData = applyRes.ok ? await applyRes.json() : { items: [] };
      setContributors(contribData.items || []);
      setInvites(inviteData.items || []);
      setApplications(applyData.items || []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }

  async function loadLanguages() {
    try {
      const res = await fetch(`${getApiPath()}/@polyglot/coverage`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const langs = [
          ...new Set((data.items || []).map((i) => i.language)),
        ].sort();
        setAvailableLanguages(
          langs.map((l) => ({ key: l, value: l, text: l })),
        );
      }
    } catch {
      // non-critical
    }
  }

  async function handleCreateInvite() {
    if (!inviteEmail || !inviteEmail.includes('@')) {
      setInviteError('Valid email required.');
      return;
    }
    if (inviteLanguages.length === 0) {
      setInviteError('Select at least one language.');
      return;
    }

    setInviteSubmitting(true);
    setInviteError(null);

    try {
      const res = await fetch(
        `${getApiPath()}/@polyglot/contributors/invite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            email: inviteEmail,
            languages: inviteLanguages,
            notes: inviteNotes || null,
          }),
        },
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setInviteError(body.error || 'Failed to create invite.');
        setInviteSubmitting(false);
        return;
      }

      const data = await res.json();
      setInviteSuccess(data.registration_url);
      setInviteSubmitting(false);
      loadData();
    } catch (err) {
      setInviteError(err.message);
      setInviteSubmitting(false);
    }
  }

  async function handleSendInvite(id) {
    try {
      const res = await fetch(
        `${getApiPath()}/@polyglot/contributors/invites/${id}/send`,
        { method: 'POST', headers: getAuthHeaders() },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
alert(body.error || 'Failed to send email.');
        return;
      }
      alert('Invitation email sent successfully.');
      loadData();
    } catch {
      alert('Failed to send invitation email.');
    }
  }

  async function handleRevokeInvite(id) {
    try {
      await fetch(`${getApiPath()}/@polyglot/contributors/invites/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      loadData();
    } catch {
      // ignore
    }
  }

  function resetInviteForm() {
    setInviteEmail('');
    setInviteLanguages([]);
    setInviteNotes('');
    setInviteError(null);
    setInviteSuccess(null);
    setShowInviteModal(false);
  }

  const statusColor = (status) => {
    const map = {
      pending: 'yellow',
      accepted: 'green',
      expired: 'grey',
      revoked: 'red',
    };
    return map[status] || 'grey';
  };

  return (
    <Container style={{ paddingTop: '2em' }}>
      <Helmet title="Contributor Management" />
      <div className="polyglot-page-header">
        <h1>Contributor Management</h1>
        <p className="subtitle">Invite and manage translation contributors</p>
      </div>

      {error && (
        <Message error>{error}</Message>
      )}

      {error ? null : (<>
      <Menu pointing secondary>
        <Menu.Item
          active={activeTab === 'contributors'}
          onClick={() => setActiveTab('contributors')}
        >
          Contributors ({contributors.length})
        </Menu.Item>
        <Menu.Item
          active={activeTab === 'invites'}
          onClick={() => setActiveTab('invites')}
        >
          Invitations ({invites.length})
        </Menu.Item>
        <Menu.Item
          active={activeTab === 'applications'}
          onClick={() => setActiveTab('applications')}
        >
          Applications ({applications.filter((a) => a.status === 'pending').length})
        </Menu.Item>
        <Menu.Menu position="right">
          <Menu.Item>
            <Button primary size="small" onClick={() => setShowInviteModal(true)}>
              <Icon name="plus" /> New Invitation
            </Button>
          </Menu.Item>
        </Menu.Menu>
      </Menu>

      {loading && <Segment loading placeholder style={{ minHeight: '150px' }} />}

      {/* Contributors Tab */}
      {!loading && activeTab === 'contributors' && (
        <Table celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell>Languages</Table.HeaderCell>
              <Table.HeaderCell>Contributions</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Since</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {contributors.map((c) => (
              <Table.Row key={c.id}>
                <Table.Cell>
                  <Link to={`/polyglot/contributors/${c.id}`}>
                    <Icon name="user" /> {c.fullname}
                  </Link>
                </Table.Cell>
                <Table.Cell>{c.email}</Table.Cell>
                <Table.Cell>
                  {(c.languages || []).map((l) => (
                    <Label key={l.code} size="small" color="blue">
                      {l.code}
                    </Label>
                  ))}
                </Table.Cell>
                <Table.Cell textAlign="center">{c.total_contributions}</Table.Cell>
                <Table.Cell>
                  <Label color={c.active ? 'green' : 'red'} size="small">
                    {c.active ? 'Active' : 'Inactive'}
                  </Label>
                </Table.Cell>
                <Table.Cell>
                  {new Date(c.created_at).toLocaleDateString()}
                </Table.Cell>
              </Table.Row>
            ))}
            {contributors.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan="6" textAlign="center">
                  No contributors yet. Send an invitation to get started.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      )}

      {/* Invites Tab */}
      {!loading && activeTab === 'invites' && (
        <Table celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell>Languages</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Invited By</Table.HeaderCell>
              <Table.HeaderCell>Created</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {invites.map((inv) => (
              <Table.Row key={inv.id}>
                <Table.Cell>{inv.email}</Table.Cell>
                <Table.Cell>
                  {(inv.proposed_languages || []).map((l) => (
                    <Label key={l} size="small">{l}</Label>
                  ))}
                </Table.Cell>
                <Table.Cell>
                  <Label color={statusColor(inv.status)} size="small">
                    {inv.status}
                  </Label>
                </Table.Cell>
                <Table.Cell>{inv.invited_by}</Table.Cell>
                <Table.Cell>
                  {new Date(inv.created_at).toLocaleDateString()}
                </Table.Cell>
                <Table.Cell>
                  {new Date(inv.expires_at).toLocaleDateString()}
                </Table.Cell>
                <Table.Cell textAlign="center">
                  {inv.status === 'pending' && (
                    <>
                      <Button
                        size="mini"
                        color="blue"
                        basic
                        onClick={() => handleSendInvite(inv.id)}
                        title={inv.email_sent_at ? `Sent ${new Date(inv.email_sent_at).toLocaleString()}` : 'Send invitation email'}
                      >
                        <Icon name="send" /> {inv.email_sent_at ? 'Resend' : 'Send'}
                      </Button>
                      {inv.email_sent_at && (
                        <div style={{ fontSize: '0.8em', color: '#21ba45', marginTop: '4px' }}>
                          Sent {new Date(inv.email_sent_at).toLocaleDateString()}
                        </div>
                      )}
                      <Button
                        size="mini"
                        color="red"
                        basic
                        onClick={() => handleRevokeInvite(inv.id)}
                        style={{ marginTop: inv.email_sent_at ? '4px' : 0 }}
                      >
                        Revoke
                      </Button>
                    </>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
            {invites.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan="7" textAlign="center">
                  No invitations yet.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      )}

      {/* Applications Tab */}
      {!loading && activeTab === 'applications' && (
        <Table celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell>Languages</Table.HeaderCell>
              <Table.HeaderCell>Motivation</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Applied</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {applications.map((app) => (
              <Table.Row key={app.id} positive={app.status === 'invited'} warning={app.status === 'pending'}>
                <Table.Cell>
                  <a href="#" onClick={(e) => { e.preventDefault(); setReviewApp(app); setReviewNotes(app.admin_notes || ''); }}>
                    {app.name}
                  </a>
                </Table.Cell>
                <Table.Cell>{app.email}</Table.Cell>
                <Table.Cell>
                  {(app.languages || []).map((l) => (
                    <Label key={l} size="small">{l}</Label>
                  ))}
                </Table.Cell>
                <Table.Cell style={{ maxWidth: '300px', fontSize: '0.9em' }}>
                  {app.motivation && app.motivation.length > 150
                    ? app.motivation.substring(0, 150) + '...'
                    : app.motivation}
                </Table.Cell>
                <Table.Cell>
                  <Label color={statusColor(app.status)} size="small">
                    {app.status}
                  </Label>
                </Table.Cell>
                <Table.Cell>
                  {new Date(app.created_at).toLocaleDateString()}
                </Table.Cell>
                <Table.Cell textAlign="center">
                  {app.status === 'pending' && (
                    <Button
                      size="mini"
                      color="blue"
                      basic
                      onClick={() => { setReviewApp(app); setReviewNotes(''); }}
                    >
                      Review
                    </Button>
                  )}
                  {app.status === 'reviewed' && (
                    <Button
                      size="mini"
                      color="blue"
                      onClick={() => {
                        setInviteEmail(app.email);
                        setInviteLanguages(app.languages || []);
                        setInviteNotes(`Application #${app.id}: ${app.name}`);
                        setShowInviteModal(true);
                      }}
                    >
                      <Icon name="send" /> Invite
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
            {applications.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan="7" textAlign="center">
                  No applications yet.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      )}

      {/* Invite Modal */}
      <Modal
        open={showInviteModal}
        onClose={resetInviteForm}
        size="small"
      >
        <Modal.Header>
          <Icon name="mail" /> Create Contributor Invitation
        </Modal.Header>
        <Modal.Content>
          {inviteSuccess ? (
            <Message success>
              <Message.Header>Invitation Created</Message.Header>
              <p>Click "Send" on the Invitations tab to email the link. Registration link:</p>
              <p style={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.9em' }}>
                {inviteSuccess}
              </p>
            </Message>
          ) : (
            <Form>
              {inviteError && <Message error>{inviteError}</Message>}
              <Form.Input
                label="Email Address"
                placeholder="contributor@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                type="email"
                required
              />
              <Form.Field required>
                <label>Languages</label>
                <Dropdown
                  placeholder="Select languages"
                  multiple
                  search
                  selection
                  options={availableLanguages}
                  value={inviteLanguages}
                  onChange={(e, { value }) => setInviteLanguages(value)}
                />
              </Form.Field>
              <Form.TextArea
                label="Notes (optional)"
                placeholder="Any notes for the contributor"
                value={inviteNotes}
                onChange={(e) => setInviteNotes(e.target.value)}
                rows={2}
              />
            </Form>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={resetInviteForm}>
            {inviteSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!inviteSuccess && (
            <Button
              primary
              loading={inviteSubmitting}
              onClick={handleCreateInvite}
            >
              <Icon name="send" /> Send Invitation
            </Button>
          )}
        </Modal.Actions>
      </Modal>

      {/* Application Review Modal */}
      <Modal
        open={!!reviewApp}
        onClose={() => setReviewApp(null)}
        size="small"
      >
        <Modal.Header>
          <Icon name="clipboard check" /> Review Application
        </Modal.Header>
        <Modal.Content>
          {reviewApp && (
            <>
              <Segment>
                <Header as="h4">{reviewApp.name}</Header>
                <p><strong>Email:</strong> {reviewApp.email}</p>
                <p><strong>Applied:</strong> {new Date(reviewApp.created_at).toLocaleDateString()}</p>
                <p>
                  <strong>Languages:</strong>{' '}
                  {(reviewApp.languages || []).map((l) => (
                    <Label key={l} size="small" color="blue">{l}</Label>
                  ))}
                </p>
              </Segment>

              <Segment>
                <Header as="h4">Motivation</Header>
                <p style={{ whiteSpace: 'pre-wrap' }}>{reviewApp.motivation || '(none provided)'}</p>
              </Segment>

              {reviewApp.experience && (
                <Segment>
                  <Header as="h4">Professional Experience</Header>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{reviewApp.experience}</p>
                </Segment>
              )}

              {reviewApp.cv_filename && (
                <Segment>
                  <Header as="h4">Uploaded Document</Header>
                  <p><Icon name="file pdf" /> {reviewApp.cv_filename}</p>
                </Segment>
              )}

              {reviewApp.admin_notes && reviewApp.status !== 'pending' && (
                <Segment>
                  <Header as="h4">Admin Notes</Header>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{reviewApp.admin_notes}</p>
                </Segment>
              )}

              {reviewApp.status === 'pending' && (
                <Form>
                  <Form.TextArea
                    label="Admin Notes"
                    placeholder="Your assessment — visible only to administrators"
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    rows={3}
                  />
                </Form>
              )}
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          {reviewApp?.status === 'pending' ? (
            <>
              <Button
                color="red"
                basic
                onClick={async () => {
                  try {
                    await fetch(
                      `${getApiPath()}/@polyglot/contributors/applications/${reviewApp.id}`,
                      {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                        body: JSON.stringify({ status: 'declined', admin_notes: reviewNotes || 'Declined' }),
                      },
                    );
                    setReviewApp(null);
                    loadData();
                  } catch { /* ignore */ }
                }}
              >
                <Icon name="cancel" /> Decline
              </Button>
              <Button
                color="green"
                onClick={async () => {
                  try {
                    await fetch(
                      `${getApiPath()}/@polyglot/contributors/applications/${reviewApp.id}`,
                      {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                        body: JSON.stringify({ status: 'reviewed', admin_notes: reviewNotes || 'Approved' }),
                      },
                    );
                    setReviewApp(null);
                    loadData();
                  } catch { /* ignore */ }
                }}
              >
              <Icon name="check" /> Approve
              </Button>
            </>
          ) : (
            <Button onClick={() => setReviewApp(null)}>Close</Button>
          )}
        </Modal.Actions>
      </Modal>
      </>)}
    </Container>
  );
}
